import { Button } from '@smartproxy/decoro/v2/button';
import { Card } from '@smartproxy/decoro/v2/card';
import { ArrowUpRightMediumIcon } from '@smartproxy/decoro/v2/icons';
import { Text } from '@smartproxy/decoro/v2/text';

import { cn } from '@/lib/utils';

import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';

import { PHTrackerProps } from '@/app/types';
import { phTrackerKey } from '@/integrations/trackingScripts/utils';

import { getBentoCtaLinkProps } from './getBentoCtaLinkProps';

export type BentoPricingCardProps = {
  title: string;
  price: string;
  icon: ImageStrapiProps;
  ctaLabel?: string;
  ctaHref?: string;
  ctaTarget?: '_blank';
  className?: string;
  onCtaClick?: () => void;
} & PHTrackerProps;

export const BentoPricingCard = ({
  title,
  price,
  icon,
  ctaLabel,
  ctaHref,
  ctaTarget,
  [phTrackerKey]: phTracker,
  className,
  onCtaClick,
}: BentoPricingCardProps) => {
  const ctaLinkProps = getBentoCtaLinkProps(ctaHref, ctaTarget, phTracker);

  return (
    <Card
      theme='dark'
      className={cn(
        'flex flex-row w-full items-center justify-between gap-3 rounded-2xl border-gray-dark-700 bg-gray-dark-900 p-4',
        'md:h-full md:flex-col md:items-stretch md:gap-4 md:p-6',
        className
      )}
    >
      <div className='flex min-w-0 flex-1 items-center gap-3 md:w-full'>
        <ImageStrapi
          key={icon.image?.data?.attributes?.url ?? title}
          block={icon}
          width={40}
          height={40}
          inner={{
            classNames: {
              container: 'size-10 shrink-0',
              image: 'md:size-10 size-8',
            },
          }}
        />
        <div className='flex min-w-0 flex-1 flex-col justify-center'>
          <Text skin='secondary' className='text-body-m! md:text-body-l!'>
            {title}
          </Text>
          <Text variant='subtitle' weight='semibold' skin='white'>
            {price}
          </Text>
        </div>
      </div>

      {ctaLabel && (
        <>
          <Button
            {...ctaLinkProps}
            variant='outline'
            skin='dark'
            size='small'
            aria-label={ctaLabel}
            className='size-8 shrink-0 p-0! md:hidden!'
            onClick={onCtaClick}
          >
            <ArrowUpRightMediumIcon size={20} aria-hidden />
          </Button>

          <Button
            {...ctaLinkProps}
            variant='outline'
            skin='dark'
            size='small'
            width='100%'
            className='hidden! w-full md:inline-flex!'
            onClick={onCtaClick}
          >
            {ctaLabel}
          </Button>
        </>
      )}
    </Card>
  );
};
